import { z } from 'zod'

export const zCouponStatus = z.enum(['unused', 'used', 'expired'])

// Campaign trigger reasons for coupon issuance
export const zIssueReason = z.enum([
	'new_member',
	'birthday',
	'admin_triggered',
	'point_milestone',
	'campaign',
	'renewal',
	'migration'
])

// Coupon master validity / use limit types
export const zCouponValidityType = z.enum(['from_issue_date', 'specified_period'])
export const zCouponUseLimitType = z.enum(['once', 'unlimited', 'custom'])

// Coupon master schema
export const zCouponMaster = z.object({
	couponMasterId: z.number(),
	couponType: z.string(), // like "cafe_ticket", "companion_ticket","goods_discount_ticket" etc

	// UI display fields
	title: z.string(),
	image: z.string().nullable(),
	emojiDisplay: z.string().nullable(),
	description: z.string().nullable(),
	userGuide: z.string().nullable(),
	sortOrder: z.number(),

	// Validity and usage rules
	validityType: zCouponValidityType,
	defaultExpiryMonths: z.number().nullable(),
	periodStartAt: z.coerce.date().nullable(),
	periodEndAt: z.coerce.date().nullable(),
	useLimitType: zCouponUseLimitType,
	maxUsesPerMember: z.number().nullable(),
	exchangePoints: z.number().nullable(),
	isActive: z.boolean(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date()
})

// Base coupon schema matching the updated MemberCoupon model
export const zMemberCoupon = z.object({
	couponId: z.number(),
	memberId: z.number(),
	couponMasterId: z.number(),
	issuedAt: z.coerce.date(),
	expiresAt: z.coerce.date(),
	issueReason: zIssueReason,
	usedCount: z.number(),
	isExpired: z.boolean(),
	status: zCouponStatus,
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date()
})

export const zMemberCouponUsageHistory = z.object({
	usageId: z.number(),
	memberCouponId: z.coerce.number(),
	usedAt: z.coerce.date(),
	createdAt: z.coerce.date()
})

// Coupon with member info and coupon master for list views
export const zMemberCouponWithMember = zMemberCoupon.extend({
	member: z
		.object({
			memberId: z.number(),
			displayName: z.string().nullable(),
			memberCode: z.string().nullable(),
			picUrl: z.string().nullable()
		})
		.optional(),
	couponMaster: zCouponMaster
		.pick({
			couponMasterId: true,
			couponType: true,
			title: true,
			image: true,
			validityType: true,
			defaultExpiryMonths: true,
			useLimitType: true,
			maxUsesPerMember: true
		})
		.optional()
})

export type CouponStatus = z.infer<typeof zCouponStatus>
export type IssueReason = z.infer<typeof zIssueReason>
export type CouponValidityType = z.infer<typeof zCouponValidityType>
export type CouponUseLimitType = z.infer<typeof zCouponUseLimitType>
export type CouponMaster = z.infer<typeof zCouponMaster>
export type MemberCoupon = z.infer<typeof zMemberCoupon>
export type MemberCouponWithMember = z.infer<typeof zMemberCouponWithMember>
export type MemberCouponUsageHistory = z.infer<typeof zMemberCouponUsageHistory>
